import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api, { HTTP_UNAUTHORIZED } from './api';

/**
 * AuthProvider
 *
 * Security notes:
 * - The JWT is stored ONLY in an httpOnly + secure + SameSite=Lax cookie
 *   (set by the backend on /api/auth/login). It is never exposed to JS.
 * - The user object (name, email, role) is kept in **memory only** — there is
 *   intentionally no localStorage / sessionStorage cache. On every reload the
 *   session is re-validated against /api/auth/me using the cookie.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      // Cookie is set by the server; we only keep the user object in memory
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Login failed';
      return { ok: false, error: detail };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Network errors on logout are non-fatal — we still clear local state
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Logout API failed (continuing local sign-out):', error?.message || error);
      }
    }
    setUser(null);
  }, []);

  // Validate session on mount via /auth/me (cookie is sent automatically)
  useEffect(() => {
    let cancelled = false;
    api
      .get('/auth/me')
      .then(({ data }) => {
        if (!cancelled) setUser(data);
      })
      .catch((error) => {
        if (cancelled) return;
        // HTTP_UNAUTHORIZED simply means no valid session — expected on first visit.
        // Other errors get surfaced in development so we can diagnose them.
        if (
          error?.response?.status !== HTTP_UNAUTHORIZED &&
          typeof process !== 'undefined' &&
          process.env.NODE_ENV === 'development'
        ) {
          // eslint-disable-next-line no-console
          console.error('Session validation error:', error?.message || error);
        }
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setBootstrapped(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, loading, bootstrapped }),
    [user, login, logout, loading, bootstrapped],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
